// Header.tsx — the persistent top bar of the app frame. Frosted, safe-area
// aware. Holds the wordmark and the utility entries (Inspire, your assessments,
// Settings). Journey now lives in the bottom tab bar, next to Today.
import { Wordmark, Icon } from './icons';
import { Pressable } from './ui';
import { Capacitor } from '@capacitor/core';
import { openExternal } from '@/services/openExternal';

// Web only: the wordmark links to renurture.org. In the native apps it is not a link, because
// the website sells membership and App Review (3.1.1) treats that as an outside purchase route.
const WORDMARK_LINKS = !Capacitor.isNativePlatform();
const openWebsite = () => openExternal('https://www.renurture.org');

const ICON_BTN = {
  width: 40,
  height: 40,
  borderRadius: 999,
  border: '1px solid var(--hair)',
  background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'var(--edge), var(--sh1)',
} as const;

export function Header({
  onOpenProgress,
  showProgress = true,
  onOpenSettings,
  onInspire,
  onOpenHelp,
}: {
  onOpenProgress: () => void;
  /** TODAY_ONE_JOB (native): false hides the "Your recovery" chart icon until the
      user has progress worth opening — a day-one tap lands in an empty room. */
  showProgress?: boolean;
  onOpenSettings: () => void;
  onInspire: () => void;
  onOpenHelp: () => void;
}) {
  return (
    <header
      style={{
        // Sticky (in-flow) so it lives inside the single scroll surface and pins to the
        // top while content scrolls behind its frosted blur. flexShrink:0 keeps its full
        // height in the shell's flex column.
        position: 'sticky',
        top: 0,
        flexShrink: 0,
        zIndex: 45,
        paddingTop: 'var(--safe-top)',
        paddingLeft: 'calc(var(--safe-left) + 18px)',
        paddingRight: 'calc(var(--safe-right) + 14px)',
        height: 'calc(var(--safe-top) + var(--frame-header-h))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'var(--tabbar)',
        backdropFilter: 'blur(24px) saturate(150%)',
        WebkitBackdropFilter: 'blur(24px) saturate(150%)',
        borderBottom: '1px solid var(--hair)',
        boxShadow: '0 0.5px 0 rgba(255,255,255,0.6) inset, 0 12px 26px -20px rgba(74,52,24,0.18)',
      }}
    >
      {WORDMARK_LINKS ? (
        <Pressable
          as="button"
          onClick={openWebsite}
          ariaLabel="Open the renurture website"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
        >
          <Wordmark h={18} />
        </Pressable>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Wordmark h={18} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Pressable
          as="button"
          onClick={onOpenHelp}
          ariaLabel="Get help — support lines and your organisation"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 40,
            padding: '0 14px',
            borderRadius: 999,
            border: '1px solid var(--hair)',
            background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
            color: 'var(--green-deep)',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 13.5,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: 'var(--edge), var(--sh1)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="4" />
            <path d="M4.9 4.9l4.2 4.2M14.9 14.9l4.2 4.2M14.9 9.1l4.2-4.2M4.9 19.1l4.2-4.2" />
          </svg>
          Help
        </Pressable>
        <Pressable
          as="button"
          onClick={onInspire}
          ariaLabel="Inspire me — with the words of others"
          style={{ ...ICON_BTN, color: 'var(--amber-deep)' }}
        >
          <Icon name="sparkle" size={18} />
        </Pressable>
        {showProgress && (
          <Pressable
            as="button"
            onClick={onOpenProgress}
            ariaLabel="Your recovery"
            style={{ ...ICON_BTN, color: 'var(--ink-soft)' }}
          >
            <Icon name="chart" size={18} />
          </Pressable>
        )}
        <Pressable
          as="button"
          onClick={onOpenSettings}
          ariaLabel="Settings"
          style={{ ...ICON_BTN, color: 'var(--ink-soft)' }}
        >
          <Icon name="settings" size={18} />
        </Pressable>
      </div>
    </header>
  );
}
